import { type FC, type FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { FormProvider, type RegisterOptions, type SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTheme } from '@mui/material/styles'
import { addDays, differenceInCalendarDays, differenceInCalendarMonths, getHours, isSameDay, isValid } from 'date-fns'
import { type Locale } from 'invictus-sdk-typescript'

import ControlDropdown from '@Components/container/form/ControlDropdown/ControlDropdown'
import ControlInputDate from '@Components/container/form/ControlInputDate/ControlInputDate'
import Loading from '@Components/presentational/loading/Loading'
import { LargeDown, LargeUp } from '@Components/utils/media/Responsive'
import {
  DEFAULT_TIME,
  MIN_DISPLAYED_TIME,
  MIN_DISPLAYED_TIME_PER_HOUR_MODE,
} from '@DS/components/components/datePicker/DatePicker.constants'
import { type InitDate, type StepHoursMode } from '@DS/components/components/datePicker/DatePicker.types'
import Separator from '@DS/components/components/foundations/shapes/separator/Separator'
import ConditionalWrapper from '@DS/components/components/utils/wrapper/ConditionalWrapper'
import BottomActions from '@DS/components/forms/button/bottomActions/BottomActions'
import Button from '@DS/components/forms/button/Button'
import { type DropdownOption } from '@DS/components/forms/dropdown/Dropdown.types'
import { useIsInModal } from '@DS/components/navigation/modal/ModalContext'
import ScrollableContent from '@DS/components/navigation/modal/ScrollableContent'
import { parseDateToISOLocalDate } from '@Utils/date'
import {
  generateHours,
  getDateHourWithDefault,
  getDefaultMaxDate,
  isDateOutsideRestrictedList,
  transformDate,
} from '@Utils/datepicker'
import { type WithPartial } from '@Utils/type'

import ControlDatePicker, { type ControlDatePickerProps } from './_internal/ControlDatePicker'
import { type JourneyDatePickerFormData } from './JourneyDatePicker.types'
import { useDatetimeDropdownMenu } from './useDatetimeDropdownMenu'
import * as styles from './styles'

export type JourneyDatePickerFormProps = Pick<
  ControlDatePickerProps,
  'initTarget' | 'maxDate' | 'mode' | 'restrictedDatesList'
> & {
  hasTimeHidden?: boolean
  initEndDate?: WithPartial<InitDate, 'date'>
  initStartDate?: InitDate
  isInwardDateVisible?: boolean
  isLoading?: boolean
  isOutwardDateVisible?: boolean
  stepHoursMode?: StepHoursMode
  onSubmit: SubmitHandler<JourneyDatePickerFormData>
}

const JourneyDatePickerForm: FC<JourneyDatePickerFormProps> = ({
  hasTimeHidden = false,
  initEndDate,
  initStartDate,
  initTarget,
  isInwardDateVisible = false,
  isLoading = false,
  isOutwardDateVisible = false,
  maxDate = getDefaultMaxDate(),
  mode,
  restrictedDatesList,
  stepHoursMode = 1,
  onSubmit,
}) => {
  const theme = useTheme()
  const { formatMessage, locale } = useIntl()
  const isInModal = useIsInModal()

  const getMinDisplayedHour = (stepHoursMode: StepHoursMode) =>
    stepHoursMode === 1 ? MIN_DISPLAYED_TIME_PER_HOUR_MODE : MIN_DISPLAYED_TIME

  const formRef = useRef<HTMLFormElement | null>(null)
  const scrollElementRef = useRef<HTMLDivElement>(null)

  const { getDropdownMenuProps } = useDatetimeDropdownMenu(formRef)

  const generateStartDateHours = (startDate: Date, stepHoursMode: StepHoursMode) => {
    const today = new Date()
    const todayHours = getHours(today)
    const todayMinTime = todayHours - (todayHours % stepHoursMode)
    const startHour = isSameDay(startDate, today) ? todayMinTime : getMinDisplayedHour(stepHoursMode)

    return generateHours(startHour, stepHoursMode, locale as Locale)
  }

  const [startHoursOptions, setStartHoursOptions] = useState(
    initStartDate?.date ? generateStartDateHours(initStartDate.date, stepHoursMode) : [],
  )
  const [endHoursOptions, setEndHoursOptions] = useState<DropdownOption[]>([])
  const [showStartDateHour, setShowStartDateHour] = useState(!!initStartDate?.date)
  const [showEndDateHour, setShowEndDateHour] = useState(!!initEndDate?.date)

  const { isDateMandatory: isStartDateMandatory = true, inputDateLabel: startDateLabel } = initStartDate || {}

  const methods = useForm<JourneyDatePickerFormData>({
    defaultValues: {
      startDate: initStartDate?.date ? parseDateToISOLocalDate(initStartDate.date) : '',
      endDate: initEndDate?.date ? parseDateToISOLocalDate(initEndDate.date) : '',
      startDateTime: initStartDate?.date ? getDateHourWithDefault({ date: initStartDate.date, stepHoursMode }) : '',
      endDateTime: initEndDate?.date ? getDateHourWithDefault({ date: initEndDate.date, stepHoursMode }) : '',
    },
    mode: 'onChange',
  })

  const { control, handleSubmit, setValue, getValues, clearErrors, trigger, watch } = methods

  const generateEndDateHours = useCallback(
    (startDate: Date, endDate: Date) => {
      const startDateTime = getValues('startDateTime')
      const startHour = isSameDay(endDate, startDate) ? +startDateTime + 2 : getMinDisplayedHour(stepHoursMode)

      return generateHours(startHour, stepHoursMode, locale as Locale)
    },
    [getValues, stepHoursMode, locale],
  )

  const calculateStartDateTime = (startDate: Date, shouldResetTime?: boolean) => {
    const startDateTime = getValues('startDateTime')

    const today = new Date()
    const initTime = shouldResetTime ? DEFAULT_TIME : +startDateTime
    const newStartDateHours = isSameDay(startDate, today)
      ? Math.max(getHours(today) - (getHours(today) % stepHoursMode), initTime)
      : initTime

    setStartHoursOptions(generateStartDateHours(startDate, stepHoursMode))
    setValue('startDateTime', `${newStartDateHours}`)
  }

  const calculateEndDateTime = useCallback(
    (endDate: Date) => {
      const [startDate, startDateTime, endDateTime] = getValues(['startDate', 'startDateTime', 'endDateTime'])

      if (!startDate) {
        return
      }

      const formattedStartDate = transformDate(startDate)
      const today = new Date()
      const minEndDate = differenceInCalendarDays(today, formattedStartDate) < 0 ? formattedStartDate : today
      let newEndDate = endDate
      let newEndDateHours = +endDateTime || DEFAULT_TIME

      if (isSameDay(minEndDate, endDate)) {
        if (+startDateTime === 22) {
          newEndDate = addDays(endDate, 1)
        } else {
          newEndDateHours = +startDateTime + stepHoursMode
        }
      }

      setValue('endDate', parseDateToISOLocalDate(newEndDate), { shouldValidate: false })
      setEndHoursOptions(generateEndDateHours(minEndDate, newEndDate))
      setValue('endDateTime', `${newEndDateHours}`)
    },
    [generateEndDateHours, getValues, setValue, stepHoursMode],
  )

  const startDateTimeWatcher = watch('startDateTime')

  useEffect(() => {
    const endDate = getValues('endDate')

    if (endDate) {
      calculateEndDateTime(transformDate(endDate))
    }
  }, [calculateEndDateTime, getValues, startDateTimeWatcher])

  const isDatesChronologyOk = () => {
    const [startDate, endDate] = getValues(['startDate', 'endDate'])

    const formattedStartDate = transformDate(startDate)
    const formattedEndDate = transformDate(endDate)

    return (
      isValid(formattedStartDate) &&
      isValid(formattedEndDate) &&
      differenceInCalendarDays(formattedEndDate, formattedStartDate) >= 0
    )
  }

  const dateValidator = (date: string, isEndDate = false) => {
    const formatedDate = transformDate(date)
    const today = new Date()

    if (date === '' || (!isEndDate && mode === 'INWARD')) {
      return 'valid'
    }

    if (!isValid(formatedDate)) {
      return 'invalid'
    }

    if (isDateOutsideRestrictedList(formatedDate, restrictedDatesList)) {
      return 'unselectable'
    }

    if (differenceInCalendarDays(formatedDate, today) < 0) {
      return 'pastDate'
    }

    if (differenceInCalendarDays(formatedDate, maxDate) > 0) {
      return 'dateTooFar'
    }

    if (isEndDate && !isDatesChronologyOk()) {
      return 'chronologyError'
    }

    if (isEndDate && isValid(formatedDate)) {
      calculateEndDateTime(formatedDate)
    } else if (!isEndDate && isValid(formatedDate)) {
      calculateStartDateTime(formatedDate)
    }
    const [startDate, endDate] = getValues(['startDate', 'endDate'])
    setShowStartDateHour(isValid(transformDate(startDate)))
    setShowEndDateHour(isValid(transformDate(endDate)))

    if (isEndDate && !isSameDay(formatedDate, transformDate(startDate))) {
      void trigger('startDate')
    }

    return 'valid'
  }

  const isEndDateRequired = !!initEndDate?.isDateMandatory || mode === 'INWARD'

  const datePickerMaxDate = mode === 'OUTWARD' && initEndDate?.date ? initEndDate.date : maxDate
  const today = new Date()
  const differenceMonths = differenceInCalendarMonths(datePickerMaxDate, today)

  const dateValidationRules = {
    valid: true,
    invalid: formatMessage({ id: 'datepicker_error_invalidDate' }),
    pastDate: formatMessage({ id: 'datepicker_error_pastDate' }),
    unselectable: formatMessage({ id: 'datepicker_error_unselectable' }),
    dateTooFar: formatMessage({ id: 'datepicker_error_dateTooFar' }, { month: differenceMonths }),
    chronologyError: formatMessage({ id: 'datepicker_error_chronologyError' }),
  }

  const startDateFormRules: RegisterOptions = {
    required: isStartDateMandatory && formatMessage({ id: 'datepicker_error_invalidDate' }),
    validate: (date: string) => dateValidationRules[dateValidator(date, false)],
  }

  const endDateFormRules: RegisterOptions = {
    required: isEndDateRequired && formatMessage({ id: 'datepicker_error_invalidDate' }),
    validate: (date: string) => dateValidationRules[dateValidator(date, true)],
  }

  useEffect(() => {
    if (mode === 'SINGLE_END_DATE' || mode === 'SINGLE_START_DATE') {
      setValue('startDate', initStartDate?.date ? parseDateToISOLocalDate(initStartDate.date) : '')
    }
  }, [setValue, initStartDate?.date, mode])

  useEffect(() => {
    if (mode === 'SINGLE_END_DATE') {
      setValue('endDate', initEndDate?.date ? parseDateToISOLocalDate(initEndDate.date) : '')
    }
  }, [setValue, initEndDate?.date, mode])

  const handleDatePickerChange = (selectedStartDate: Date, selectedEndDate?: Date) => {
    clearErrors()

    const [startDate, endDate] = getValues(['startDate', 'endDate'])

    if (!selectedEndDate && initEndDate?.isDateMandatory) {
      if (differenceInCalendarDays(transformDate(endDate), selectedStartDate) < 0) {
        const dayAfter = addDays(selectedStartDate, 1)
        setValue('endDate', parseDateToISOLocalDate(dayAfter), {
          shouldValidate: true,
        })
        calculateEndDateTime(dayAfter)
      }
    } else if (!selectedEndDate && !initEndDate?.isDateMandatory) {
      setValue('endDate', '')
      setValue('endDateTime', '')
    }

    if (!isSameDay(selectedStartDate, transformDate(startDate))) {
      setValue('startDate', parseDateToISOLocalDate(selectedStartDate), { shouldValidate: true })
      calculateStartDateTime(selectedStartDate, true)
    }

    if (mode === 'SINGLE_END_DATE') {
      setValue('startDate', initStartDate?.date ? parseDateToISOLocalDate(initStartDate.date) : '')
    }

    if (selectedEndDate) {
      calculateEndDateTime(selectedEndDate)
    }

    setShowEndDateHour(!!selectedEndDate)
  }

  const onClearEndDate = () => setShowEndDateHour(false)

  const getOptionLabel = (options: DropdownOption[], value: string) => options.find((opt) => opt.value === value)?.label

  const onSubmitHandler = (event: FormEvent) => {
    event.preventDefault()
    event.stopPropagation()

    return handleSubmit((data) => onSubmit(data))(event)
  }

  return (
    <Loading isLoading={isLoading}>
      <FormProvider {...methods}>
        <form onSubmit={onSubmitHandler} css={styles.journeyDatePickerFormRoot} ref={formRef}>
          <ConditionalWrapper
            condition={isInModal}
            wrapper={(children) => (
              <ScrollableContent ref={scrollElementRef} hasNoContentSpacing>
                {children}
              </ScrollableContent>
            )}
          >
            <div css={styles.controlsContainer(theme, isInModal)}>
              {isOutwardDateVisible && (
                <div css={styles.controlsRow}>
                  <div css={styles.controlInputDate}>
                    <ControlInputDate
                      autoComplete="off"
                      disabled={mode === 'INWARD'}
                      label={startDateLabel || formatMessage({ id: 'datepicker_outward_label' })}
                      name="startDate"
                      rules={startDateFormRules}
                    />
                  </div>
                  {!hasTimeHidden && showStartDateHour && (
                    <div css={styles.controlDropdown(theme)}>
                      <ControlDropdown
                        disabled={mode === 'INWARD'}
                        isLabelHidden
                        inputProps={{
                          'data-test': 'select-startDateTime',
                          'aria-label': `${formatMessage({ id: 'trip_outwardTimeLabel' })}, ${getOptionLabel(
                            startHoursOptions,
                            getValues('startDateTime'),
                          )}`,
                        }}
                        MenuProps={getDropdownMenuProps()}
                        name="startDateTime"
                        options={startHoursOptions}
                      />
                    </div>
                  )}
                </div>
              )}
              {isInwardDateVisible && (
                <div css={styles.controlsRow}>
                  <div css={styles.controlInputDate}>
                    <ControlInputDate
                      autoComplete="off"
                      buttonAriaLabel={formatMessage({ id: 'input_clearButton_inward_ariaLabel' })}
                      disabled={mode === 'OUTWARD'}
                      isEndAdornmentHidden={mode === 'OUTWARD'}
                      label={initEndDate?.inputDateLabel || formatMessage({ id: 'datepicker_inward_label' })}
                      name="endDate"
                      onClear={onClearEndDate}
                      rules={endDateFormRules}
                    />
                  </div>
                  {!hasTimeHidden && showEndDateHour && endHoursOptions.length > 0 && (
                    <div css={styles.controlDropdown(theme)}>
                      <ControlDropdown
                        disabled={mode === 'OUTWARD'}
                        isLabelHidden
                        inputProps={{
                          'data-test': 'select-endDateTime',
                          'aria-label': `${formatMessage({ id: 'trip_inwardTimeLabel' })}, ${getOptionLabel(
                            endHoursOptions,
                            getValues('endDateTime'),
                          )}`,
                        }}
                        MenuProps={getDropdownMenuProps()}
                        name="endDateTime"
                        options={endHoursOptions}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div css={styles.datePickerContainer(theme)}>
              <ControlDatePicker
                control={control}
                initTarget={initTarget}
                maxDate={datePickerMaxDate}
                mode={mode}
                numberOfMonths={differenceInCalendarMonths(maxDate, new Date()) + 1}
                onChange={handleDatePickerChange}
                restrictedDatesList={restrictedDatesList}
                scrollElementRef={scrollElementRef}
              />
            </div>
          </ConditionalWrapper>
          <LargeUp>
            <Separator size="extraSmall" isNotOutline />
            <div css={styles.buttonWrapper(theme)}>
              <Button type="submit">
                <FormattedMessage id="validate" />
              </Button>
            </div>
          </LargeUp>
          <LargeDown>
            <BottomActions
              primary={{
                type: 'submit',
                children: <FormattedMessage id="validate" />,
              }}
            />
          </LargeDown>
        </form>
      </FormProvider>
    </Loading>
  )
}

export default JourneyDatePickerForm
